import {
  defaultDropAnimationSideEffects,
  rectIntersection,
} from '@dnd-kit/core';
import { Typography } from '@material-tailwind/react';
import { type FC, useMemo } from 'react';
import type { PipelineBoardProps } from 'src/modules/communications/types/pipeline/board.type';
import { DraggableBoard } from 'src/modules/shared/components/organisms/board';
import { useAiWorkspaceStatusIdsByStatus } from 'src/modules/shared/hooks/api/automations/employees/workspaces-ai';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { usePipelineDnd } from '../../../../../hooks/pipeline/pipeline-dnd';
import { usePipelineColumnPreferences } from '../../../../../hooks/use-pipeline-column-preferences';
import { resolveBulkToggleConfig } from '../../../../../utils/pipeline/bulk-toggle-config.util';
import { createPipelineContainerId } from '../../../../../utils/pipeline/dnd.utils';
import { ConversationCardRenderer } from '../../../../molecules/conversations/conversation-card-renderer';
import { PipelineStatusColumn } from '../../../../molecules/pipelines/status-column';
import { AiStatusesRailToggle } from './ai-statuses-rail-toggle';
import { PipelineBoardSkeleton } from './pipeline-board-skeleton';

const dropAnimation = {
  duration: 200,
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

/**
 * Renders the responsive funnel board with shared drag-and-drop behavior.
 * @param props - Funnel filters, selection, and layout callbacks.
 * @returns The conversation pipeline presentation.
 */
export const PipelineBoard: FC<PipelineBoardProps> = ({
  statuses,
  selectedFunnelId,
  searchTerm,
  onConversationSelect,
  onOpenContactProfile,
  selectedConversationId,
  filters,
  sorts,
  className = '',
  aiStatusIds = [],
}) => {
  const { t } = useI18n();
  const {
    activeConversation,
    activeStatusId,
    shouldAnimate,
    isUpdating,
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  } = usePipelineDnd({
    onError: (error) => {
      console.error('Failed to update conversation status:', error);
    },
  });

  const shouldFetchAiStatusIdsFromWorkspace =
    aiStatusIds.length === 0 && Boolean(selectedFunnelId);

  const { aiStatusIds: workspaceAiStatusIds } = useAiWorkspaceStatusIdsByStatus(
    statuses.map((status) => status.id),
    shouldFetchAiStatusIdsFromWorkspace
  );

  const resolvedAiStatusIds = shouldFetchAiStatusIdsFromWorkspace
    ? workspaceAiStatusIds
    : aiStatusIds;
  const aiStatusIdSet = useMemo(
    () => new Set(resolvedAiStatusIds),
    [resolvedAiStatusIds]
  );

  const {
    loading: loadingPreferences,
    areAllAiStatusesCollapsed,
    areAllStatusesCollapsed,
    isStatusCollapsed,
    toggleStatus,
    collapseAiStatuses,
    expandAiStatuses,
    collapseAllStatuses,
    expandAllStatuses,
  } = usePipelineColumnPreferences({
    funnelId: selectedFunnelId,
    statusIds: statuses.map((status) => status.id),
    aiStatusIds: resolvedAiStatusIds,
  });

  const hasAiStatuses = resolvedAiStatusIds.length > 0;
  const bulkToggleConfig = useMemo(
    () =>
      resolveBulkToggleConfig({
        hasAiStatuses,
        areAllAiStatusesCollapsed,
        areAllStatusesCollapsed,
        showAiStatusesLabel: t(
          'communications.pipeline.actions.showAiStatuses'
        ),
        hideAiStatusesLabel: t(
          'communications.pipeline.actions.hideAiStatuses'
        ),
        showAllStatusesLabel: t(
          'communications.pipeline.actions.showAllStatuses'
        ),
        hideAllStatusesLabel: t(
          'communications.pipeline.actions.hideAllStatuses'
        ),
        expandAiStatuses,
        collapseAiStatuses,
        expandAllStatuses,
        collapseAllStatuses,
      }),
    [
      hasAiStatuses,
      areAllAiStatusesCollapsed,
      areAllStatusesCollapsed,
      t,
      expandAiStatuses,
      collapseAiStatuses,
      expandAllStatuses,
      collapseAllStatuses,
    ]
  );
  const statusColumns = useMemo(() => {
    if (!selectedFunnelId) return null;

    return (
      <div className="flex h-full min-h-0">
        <div className="flex h-full">
          <AiStatusesRailToggle
            hasAiStatuses={hasAiStatuses}
            isCollapsed={bulkToggleConfig.isCollapsed}
            label={bulkToggleConfig.label}
            onClick={bulkToggleConfig.onClick}
          />
          {statuses.map((status) => (
            <PipelineStatusColumn
              key={status.id}
              status={status}
              selectedFunnelId={selectedFunnelId}
              searchTerm={searchTerm}
              selectedConversationId={selectedConversationId}
              onConversationSelect={onConversationSelect}
              onOpenContactProfile={onOpenContactProfile}
              isHighlighted={
                activeStatusId === createPipelineContainerId(status.id)
              }
              filters={filters}
              sorts={sorts}
              isAiLinked={aiStatusIdSet.has(status.id)}
              isCollapsed={isStatusCollapsed(status.id)}
              onToggleCollapsed={() => toggleStatus(status.id)}
            />
          ))}
        </div>
      </div>
    );
  }, [
    statuses,
    selectedFunnelId,
    searchTerm,
    selectedConversationId,
    onConversationSelect,
    onOpenContactProfile,
    activeStatusId,
    filters,
    sorts,
    hasAiStatuses,
    aiStatusIdSet,
    bulkToggleConfig,
    isStatusCollapsed,
    toggleStatus,
  ]);

  if (!selectedFunnelId) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <Typography className="text-sm">
          {t('communications.pipeline.empty.noFunnel')}
        </Typography>
      </div>
    );
  }

  if (loadingPreferences) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <PipelineBoardSkeleton />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DraggableBoard
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={(event) => {
          void handleDragEnd(event);
        }}
        onDragCancel={handleDragCancel}
        collisionDetection={rectIntersection}
        dropAnimation={shouldAnimate ? dropAnimation : null}
        dragOverlay={
          activeConversation && (
            <div
              className={`transform-gpu will-change-transform w-[288px] opacity-80 grayscale ${
                isUpdating ? 'opacity-50' : ''
              }`}
              style={{
                cursor: 'grabbing',
                touchAction: 'none',
                pointerEvents: 'none',
              }}
            >
              <ConversationCardRenderer
                conversation={activeConversation}
                isSelected={selectedConversationId === activeConversation.id}
                layout="pipeline"
                cacheOnly
              />
            </div>
          )
        }
        className={className}
      >
        {statusColumns}
      </DraggableBoard>
    </div>
  );
};
